import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { supabase } from "../lib/supabaseClient";
import { useCreditBalance } from "../hooks/useCreditBalance";
import CreditPurchaseModal from "./CreditPurchaseModal";

const PLANNING_COST_CREDITS = 40;

// ─── Typen ────────────────────────────────────────────────────────────────────

interface FixerPunkt {
  datum: string;
  zeit: string;
  beschreibung: string;
  ort: string;
}

interface VorhandeneAktivitaet {
  datum: string | null;
  titel: string;
  ort: string | null;
  start_zeit: string | null;
  end_zeit: string | null;
}

interface PlanActivity {
  titel: string;
  beschreibung: string;
  ort: string | null;
  kategorie: string;
  zeit_von: string | null;
  zeit_bis: string | null;
  prioritaet: string;
  accepted: boolean;
}

interface PlanDay {
  tag_nummer: number;
  datum: string;
  aktivitaeten: PlanActivity[];
}

interface TripPlan {
  meta: { reise_titel: string; zusammenfassung: string };
  tage: PlanDay[];
}

interface Props {
  mode: "new_trip" | "activities";
  trip?: { id: string; start_date: string; end_date: string; destination?: string };
  userId: string;
  onClose: (created?: boolean) => void;
}

// ─── Konstanten ───────────────────────────────────────────────────────────────

const MOBILITAET_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "mietauto", label: "Mietauto" },
  { value: "flug", label: "Flug" },
  { value: "zug", label: "Zug" },
  { value: "bus", label: "Bus" },
  { value: "schiff", label: "Schiff/Fähre" },
  { value: "wohnmobil", label: "Wohnmobil" },
  { value: "fahrrad", label: "Fahrrad" },
  { value: "wandern", label: "Wandern" },
  { value: "oev", label: "Öffentliche Verkehrsmittel" },
];

const INTERESSEN_VORSCHLAEGE = [
  "Wandern", "Natur", "Kultur", "Museen", "Kulinarik", "Sport",
  "Strand", "Berge", "Städtetrip", "Abenteuer", "Entspannung",
  "Geschichte", "Architektur", "Shopping", "Nightlife", "Familienfreundlich",
];

const TEILNEHMER_KATEGORIEN = [
  { value: "kinder", label: "Kinder" },
  { value: "jugendliche", label: "Jugendliche" },
  { value: "erwachsene", label: "Erwachsene" },
  { value: "senioren", label: "Senioren" },
];

const BUDGET_OPTIONS = [
  { value: "sehr_tief", label: "Budget" },
  { value: "tief", label: "Sparsam" },
  { value: "mittel", label: "Mittel" },
  { value: "hoch", label: "Komfort" },
  { value: "luxus", label: "Luxus" },
];

const ESSEN_OPTIONS = [
  { value: "fruehstueck", label: "Frühstück" },
  { value: "mittagessen", label: "Mittagessen" },
  { value: "abendessen", label: "Abendessen" },
];

const CATEGORY_MAP: Record<string, string> = {
  reisen: "Reisen",
  uebernachten: "Übernachtung",
  verpflegung: "Verpflegung",
  aktivitaet: "Aktivitäten",
  workshop: "Workshops",
  sonstige: "Sonstiges",
};

const CATEGORY_COLORS: Record<string, string> = {
  Reisen: "bg-sky-500",
  Übernachtung: "bg-amber-500",
  Verpflegung: "bg-emerald-500",
  Aktivitäten: "bg-orange-500",
  Workshops: "bg-purple-500",
  Sonstiges: "bg-slate-500",
};

const PARAM_STEPS = 4;

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function generateJoinCode(): string {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

function geocodeLocationString(
  locationString: string
): Promise<{ lat: number; lng: number } | null> {
  if (!locationString || !window.google?.maps) return Promise.resolve(null);
  return new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: locationString }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        resolve({
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });
      } else {
        resolve(null);
      }
    });
  });
}

function buildLoadingMessages(params: {
  destination: string;
  interessen: string[];
  uebernachtungenPlanen: boolean;
  essenPlanen: string[];
  mobilitaet: string[];
}): string[] {
  const msgs = [
    `Ich erkunde ${params.destination}…`,
    "Ich plane die optimale Route…",
    "Ich prüfe Reisezeiten und Entfernungen…",
    "Ich suche nach passenden Aktivitäten…",
  ];
  if (params.interessen.length > 0)
    msgs.push(`Ich berücksichtige eure Interessen: ${params.interessen.slice(0, 3).join(", ")}…`);
  if (params.uebernachtungenPlanen)
    msgs.push("Ich plane Übernachtungsmöglichkeiten ein…");
  if (params.essenPlanen.length > 0)
    msgs.push("Ich suche nach Restaurants und Verpflegungsoptionen…");
  if (params.mobilitaet.includes("flug"))
    msgs.push("Ich berücksichtige Flugzeiten und Transfers…");
  msgs.push("Fast fertig – ich verfeinere den Plan…");
  return msgs;
}

// ─── Haupt-Komponente ─────────────────────────────────────────────────────────

export default function AiTripPlanningModal({ mode, trip, userId, onClose }: Props) {
  const navigate = useNavigate();

  type Step = "params" | "loading" | "review";
  const [step, setStep] = useState<Step>("params");
  const [paramStep, setParamStep] = useState(1);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // ── Params-State ──────────────────────────────────────────────────────────
  const [destination, setDestination] = useState(trip?.destination ?? "");
  const [startDate, setStartDate] = useState(trip?.start_date ?? "");
  const [endDate, setEndDate] = useState(trip?.end_date ?? "");
  const [mobilitaet, setMobilitaet] = useState<string[]>([]);
  const [reiseArt, setReiseArt] = useState("");
  const [budget, setBudget] = useState("mittel");
  const [teilnehmerBeschreibung, setTeilnehmerBeschreibung] = useState("");
  const [teilnehmerKategorien, setTeilnehmerKategorien] = useState<string[]>(["erwachsene"]);
  const [teilnehmerGeschlecht, setTeilnehmerGeschlecht] = useState("gemischt");
  const [interessen, setInteressen] = useState<string[]>([]);
  const [interessenInput, setInteressenInput] = useState("");
  const [uebernachtungenPlanen, setUebernachtungenPlanen] = useState(false);
  const [essenPlanen, setEssenPlanen] = useState<string[]>([]);
  const [fixePunkte, setFixePunkte] = useState<FixerPunkt[]>([]);
  const [rahmenbedingungen, setRahmenbedingungen] = useState("");

  // ── Credits ───────────────────────────────────────────────────────────────
  const { balance: creditBalance, reload: reloadCredits } = useCreditBalance();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  // ── Review-State ──────────────────────────────────────────────────────────
  const [plan, setPlan] = useState<TripPlan | null>(null);

  // ── Vorhandene Aktivitäten (activities-Mode) ───────────────────────────────
  const [vorhandeneAktivitaeten, setVorhandeneAktivitaeten] = useState<VorhandeneAktivitaet[]>([]);

  useEffect(() => {
    if (mode !== "activities" || !trip) return;
    supabase
      .from("trip_program_items")
      .select("title, location, start_time, end_time")
      .eq("trip_id", trip.id)
      .then(({ data }) => {
        if (data) {
          setVorhandeneAktivitaeten(
            data.map((item) => ({
              datum: item.start_time ? item.start_time.substring(0, 10) : null,
              titel: item.title,
              ort: item.location,
              start_zeit: item.start_time,
              end_zeit: item.end_time,
            }))
          );
        }
      });
  }, [mode, trip]);

  // ── Rotierende Lademeldungen ───────────────────────────────────────────────
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const loadingMessages = useRef<string[]>([]);

  useEffect(() => {
    if (step !== "loading") return;
    loadingMessages.current = buildLoadingMessages({
      destination,
      interessen,
      uebernachtungenPlanen,
      essenPlanen,
      mobilitaet,
    });
    setLoadingMsgIdx(0);
    const interval = setInterval(() => {
      setLoadingMsgIdx((i) => (i + 1) % loadingMessages.current.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Validierung ───────────────────────────────────────────────────────────
  const tripBoundsOk =
    mode !== "activities" ||
    !trip ||
    (startDate >= trip.start_date && endDate <= trip.end_date);

  const step1Valid =
    destination.trim() !== "" &&
    startDate !== "" &&
    endDate !== "" &&
    startDate <= endDate &&
    tripBoundsOk;

  // ── Callbacks ─────────────────────────────────────────────────────────────

  const toggleMobilitaet = useCallback((value: string) => {
    setMobilitaet((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }, []);

  const toggleInteresse = useCallback((value: string) => {
    setInteressen((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }, []);

  const addInteresseFromInput = () => {
    const trimmed = interessenInput.trim();
    if (!trimmed) return;
    const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    if (!interessen.includes(capitalized)) {
      setInteressen((prev) => [...prev, capitalized]);
    }
    setInteressenInput("");
  };

  const toggleTeilnehmerKategorie = useCallback((value: string) => {
    setTeilnehmerKategorien((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }, []);

  const toggleEssen = useCallback((value: string) => {
    setEssenPlanen((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }, []);

  const addFixerPunkt = () => {
    if (fixePunkte.length >= 5) return;
    setFixePunkte((prev) => [...prev, { datum: "", zeit: "", beschreibung: "", ort: "" }]);
  };

  const updateFixerPunkt = (idx: number, field: keyof FixerPunkt, value: string) => {
    setFixePunkte((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p))
    );
  };

  const removeFixerPunkt = (idx: number) => {
    setFixePunkte((prev) => prev.filter((_, i) => i !== idx));
  };

  const toggleActivity = (dayIdx: number, aktIdx: number) => {
    if (!plan) return;
    const newTage = plan.tage.map((tag, di) => ({
      ...tag,
      aktivitaeten: tag.aktivitaeten.map((akt, ai) =>
        di === dayIdx && ai === aktIdx ? { ...akt, accepted: !akt.accepted } : akt
      ),
    }));
    setPlan({ ...plan, tage: newTage });
  };

  // ── API-Call ──────────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!destination.trim()) {
      setError(mode === "activities" ? "Bitte gib ein Planungsgebiet an." : "Bitte gib ein Reiseziel an.");
      setParamStep(1);
      return;
    }
    if (!startDate || !endDate) {
      setError("Bitte wähle einen Reisezeitraum.");
      setParamStep(1);
      return;
    }
    if (startDate > endDate) {
      setError("Das Startdatum muss vor dem Enddatum liegen.");
      setParamStep(1);
      return;
    }

    setError("");
    setStep("loading");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Nicht angemeldet.");

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/generate-trip-plan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY ?? "",
        },
        body: JSON.stringify({
          user_id: userId,
          parameters: {
            zeitraum: { start: startDate, ende: endDate },
            destination: { beschreibung: destination },
            mobilitaet,
            reise_art: reiseArt,
            budget,
            uebernachtungen_planen: uebernachtungenPlanen,
            essen_planen: essenPlanen,
            teilnehmer: {
              beschreibung: teilnehmerBeschreibung,
              kategorien: teilnehmerKategorien,
              geschlecht: teilnehmerGeschlecht,
            },
            interessen,
            fixe_punkte: fixePunkte
              .filter((p) => p.beschreibung.trim())
              .map((p) => ({
                datum: p.datum || null,
                zeit: p.zeit || null,
                beschreibung: p.beschreibung,
                ort: p.ort || null,
              })),
            rahmenbedingungen: rahmenbedingungen
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean),
            vorhandene_aktivitaeten: vorhandeneAktivitaeten,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error ?? "Fehler beim Generieren des Reiseplans.");
        setStep("params");
        return;
      }

      // Alle Aktivitäten initial als akzeptiert markieren
      const planWithAccepted: TripPlan = {
        meta: data.meta,
        tage: (data.tage as PlanDay[]).map((tag) => ({
          ...tag,
          aktivitaeten: tag.aktivitaeten.map((akt) => ({ ...akt, accepted: true })),
        })),
      };

      setPlan(planWithAccepted);
      setStep("review");
      reloadCredits(); // Balance sofort aktualisieren
    } catch (err) {
      console.error("generate-trip-plan Fehler:", err);
      setError("Verbindungsfehler. Bitte erneut versuchen.");
      setStep("params");
    }
  };

  // ── Speichern nach Review ─────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!plan) return;

    const rawAccepted = plan.tage.flatMap((tag) =>
      tag.aktivitaeten
        .filter((akt) => akt.accepted)
        .map((akt) => ({ tag, akt }))
    );

    if (rawAccepted.length === 0) {
      setError("Bitte wähle mindestens eine Aktivität aus.");
      return;
    }

    setSaving(true);
    setError("");

    // Ortsbezeichnungen parallel geocodieren → Koordinaten für die Karte
    const coordsList = await Promise.all(
      rawAccepted.map(({ akt }) =>
        akt.ort ? geocodeLocationString(akt.ort) : Promise.resolve(null)
      )
    );

    const acceptedItems = rawAccepted.map(({ tag, akt }, i) => ({
      creator_id: userId,
      title: akt.titel,
      description: akt.beschreibung || null,
      category: CATEGORY_MAP[akt.kategorie] ?? "Sonstiges",
      location: akt.ort ?? null,
      location_lat: coordsList[i]?.lat ?? null,
      location_lng: coordsList[i]?.lng ?? null,
      scheduled_date: tag.datum,
      start_time: akt.zeit_von
        ? new Date(`${tag.datum}T${akt.zeit_von}`).toISOString()
        : null,
      end_time: akt.zeit_bis
        ? new Date(`${tag.datum}T${akt.zeit_bis}`).toISOString()
        : null,
    }));

    try {
      if (mode === "activities" && trip) {
        // Aktivitäten in bestehenden Trip einfügen
        const items = acceptedItems.map((item) => ({ ...item, trip_id: trip.id }));
        const { error: insertError } = await supabase
          .from("trip_program_items")
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert(items as any);

        if (insertError) {
          setError("Fehler beim Speichern der Aktivitäten.");
          setSaving(false);
          return;
        }

        onClose(true);
      } else {
        // Neue Reise auto-erstellen
        const joinCode = generateJoinCode();

        const { data: newTrip, error: tripError } = await supabase
          .from("trips")
          .insert({
            creator_id: userId,
            name: plan.meta.reise_titel,
            description: plan.meta.zusammenfassung,
            destination: destination,
            start_date: startDate,
            end_date: endDate,
            status: "draft",
            is_public: false,
            requires_approval: false,
            join_code: joinCode,
          })
          .select("id")
          .single();

        if (tripError || !newTrip) {
          setError("Fehler beim Erstellen der Reise.");
          setSaving(false);
          return;
        }

        // Creator als Teilnehmer eintragen
        await supabase.from("trip_participants").insert({
          trip_id: newTrip.id,
          user_id: userId,
        });

        // Programmpunkte einfügen
        const items = acceptedItems.map((item) => ({ ...item, trip_id: newTrip.id }));
        const { error: insertError } = await supabase
          .from("trip_program_items")
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert(items as any);

        if (insertError) {
          console.error("Programmpunkte konnten nicht eingefügt werden:", insertError);
        }

        onClose(true);
        navigate(`/trip/${newTrip.id}`);
      }
    } catch (err) {
      console.error("Fehler beim Speichern:", err);
      setError("Unbekannter Fehler. Bitte erneut versuchen.");
      setSaving(false);
    }
  };

  const acceptedCount = plan?.tage.flatMap((t) => t.aktivitaeten).filter((a) => a.accepted).length ?? 0;

  // ─── Render ───────────────────────────────────────────────────────────────

  const chipClass = (active: boolean) =>
    `text-xs px-3 py-1.5 rounded-full border transition-colors ${
      active
        ? "bg-sky-500/20 text-sky-400 border-sky-500/30"
        : "bg-[#1e2639] text-slate-400 border-[#2a3347] hover:text-slate-200"
    }`;

  const inputClass =
    "w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600";

  const stepTitle: Record<number, string> = {
    1: mode === "activities" ? "Zeitraum & Gebiet" : "Ziel & Zeitraum",
    2: "Reisetyp & Mobilität",
    3: "Teilnehmer & Interessen",
    4: "Details",
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] w-full max-w-lg relative max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-[#2a3347] shrink-0">
          <div className="flex items-center justify-between mb-2">
            {step === "params" && (
              <span className="text-xs text-slate-500">
                Schritt {paramStep} von {PARAM_STEPS}
              </span>
            )}
            {step !== "params" && <span />}
            <button
              onClick={() => onClose()}
              className="text-slate-600 hover:text-slate-300 text-xl transition-colors ml-auto"
              aria-label="Modal schließen"
            >
              &times;
            </button>
          </div>

          <h2 className="text-sm font-semibold text-slate-100 pr-2">
            {step === "params" && `✨ ${stepTitle[paramStep]}`}
            {step === "loading" && "✨ KI plant…"}
            {step === "review" && (plan?.meta.reise_titel ?? "Reiseplan überprüfen")}
          </h2>

          {step === "params" && (
            <div className="flex items-center justify-between mt-0.5">
              <p className="text-xs text-slate-500">
                {mode === "new_trip"
                  ? "Gib deine Reiseparameter ein – die KI erstellt einen vollständigen Tagesplan."
                  : "Die KI erstellt Aktivitätsvorschläge für diese Reise."}
              </p>
              {creditBalance !== null && (
                <span className={`text-[10px] font-medium ml-3 shrink-0 ${creditBalance < PLANNING_COST_CREDITS ? "text-amber-400" : "text-slate-500"}`}>
                  {creditBalance} SC
                </span>
              )}
            </div>
          )}
          {step === "review" && plan && (
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{plan.meta.zusammenfassung}</p>
          )}

          {/* Fortschrittsbalken (nur Params) */}
          {step === "params" && (
            <div className="flex gap-1 mt-3">
              {Array.from({ length: PARAM_STEPS }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                    i + 1 === paramStep
                      ? "bg-sky-400"
                      : i + 1 < paramStep
                      ? "bg-sky-400/40"
                      : "bg-[#2a3347]"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">

          {/* ─── Insufficient Credits Screen ───────────────────────────────── */}
          {step === "params" && creditBalance !== null && creditBalance < PLANNING_COST_CREDITS && (
            <div className="flex flex-col items-center text-center py-8 gap-4">
              <p className="text-4xl">✨</p>
              <div>
                <h3 className="text-base font-semibold text-slate-100 mb-1">Svevana Credits aufladen</h3>
                <p className="text-sm text-slate-400">
                  Eine KI-Planung kostet <span className="text-slate-200 font-medium">{PLANNING_COST_CREDITS} SC</span>.
                </p>
                <p className="text-sm text-slate-400">
                  Dein Guthaben: <span className={`font-medium ${creditBalance === 0 ? "text-slate-500" : "text-amber-400"}`}>{creditBalance} SC</span>
                </p>
              </div>
              <div className="bg-[#1e2639] rounded-xl px-4 py-3 text-xs text-slate-400 text-left w-full space-y-1">
                <p className="font-medium text-slate-300 mb-1">Pakete:</p>
                <p>• Starter: CHF 1.90 → 300 SC (7 Planungen)</p>
                <p>• Standard: CHF 4.90 → 850 SC (21 Planungen)</p>
                <p>• Premium: CHF 9.90 → 2000 SC (50 Planungen)</p>
              </div>
              <button
                onClick={() => setShowPurchaseModal(true)}
                className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold transition-colors"
              >
                Credits kaufen →
              </button>
            </div>
          )}

          {/* ─── Params: nur anzeigen wenn genug Credits ───────────────────── */}
          {(step !== "params" || creditBalance === null || creditBalance >= PLANNING_COST_CREDITS) && (<>

          {/* ─── Step: params › Schritt 1 ──────────────────────────────────── */}
          {step === "params" && paramStep === 1 && (
            <div className="space-y-5">

              {/* Zeitraum – nur activities mode: editierbar mit Bounds */}
              {mode === "activities" ? (
                <div>
                  <label className="block mb-1.5 text-xs text-slate-400 font-medium">
                    Für welchen Zeitraum? <span className="text-red-400">*</span>
                  </label>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block mb-1 text-[10px] text-slate-500">Von</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (endDate && e.target.value > endDate) setEndDate("");
                        }}
                        min={trip?.start_date}
                        max={trip?.end_date}
                        className={inputClass}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block mb-1 text-[10px] text-slate-500">Bis</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={!startDate}
                        min={startDate || trip?.start_date}
                        max={trip?.end_date}
                        className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                      />
                    </div>
                  </div>
                  {/* Schnellauswahl */}
                  {trip && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {(() => {
                        const today = new Date().toISOString().split("T")[0];
                        const buttons = [];
                        if (today >= trip.start_date && today <= trip.end_date) {
                          buttons.push(
                            <button
                              key="today"
                              type="button"
                              onClick={() => { setStartDate(today); setEndDate(today); }}
                              className="text-[10px] px-2 py-1 rounded-full bg-[#1e2639] text-slate-400 hover:text-sky-400 hover:bg-sky-500/10 transition-colors"
                            >
                              Heute
                            </button>
                          );
                        }
                        buttons.push(
                          <button
                            key="full"
                            type="button"
                            onClick={() => { setStartDate(trip.start_date); setEndDate(trip.end_date); }}
                            className="text-[10px] px-2 py-1 rounded-full bg-[#1e2639] text-slate-400 hover:text-sky-400 hover:bg-sky-500/10 transition-colors"
                          >
                            Gesamte Reise
                          </button>
                        );
                        return buttons;
                      })()}
                    </div>
                  )}
                  {/* Kontext-Hinweis */}
                  {trip && (
                    <p className="text-[10px] text-slate-600 mt-1.5">
                      Reisezeitraum: {format(new Date(trip.start_date), "dd.MM.yyyy")} – {format(new Date(trip.end_date), "dd.MM.yyyy")}
                    </p>
                  )}
                  {/* Bounds-Fehler */}
                  {trip && startDate && startDate < trip.start_date && (
                    <p className="text-red-400 text-xs mt-1">Startdatum liegt vor Reisebeginn.</p>
                  )}
                  {trip && endDate && endDate > trip.end_date && (
                    <p className="text-red-400 text-xs mt-1">Enddatum liegt nach Reiseende.</p>
                  )}
                  {startDate && endDate && startDate > endDate && (
                    <p className="text-red-400 text-xs mt-1">Enddatum muss nach dem Startdatum liegen.</p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block mb-1.5 text-xs text-slate-400 font-medium">
                    Zeitraum <span className="text-red-400">*</span>
                  </label>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block mb-1 text-[10px] text-slate-500">Von</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (endDate && e.target.value > endDate) setEndDate("");
                        }}
                        className={inputClass}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block mb-1 text-[10px] text-slate-500">Bis</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={!startDate}
                        min={startDate || undefined}
                        className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                      />
                    </div>
                  </div>
                  {startDate && endDate && startDate > endDate && (
                    <p className="text-red-400 text-xs mt-1">Enddatum muss nach dem Startdatum liegen.</p>
                  )}
                </div>
              )}

              {/* Reiseziel / Planungsgebiet */}
              <div>
                <label className="block mb-1.5 text-xs text-slate-400 font-medium">
                  {mode === "activities" ? "Planungsgebiet" : "Reiseziel"}{" "}
                  <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder={
                    mode === "activities"
                      ? "z.B. Dublin, Edinburgh, Küstenroute"
                      : "z.B. Irland, Wien, Toskana"
                  }
                  autoFocus
                  className={inputClass}
                />
                {/* Quick-Fill: Reisedestination übernehmen */}
                {mode === "activities" && trip?.destination && destination !== trip.destination && (
                  <button
                    type="button"
                    onClick={() => setDestination(trip.destination!)}
                    className="mt-1.5 text-[10px] text-slate-500 hover:text-sky-400 transition-colors flex items-center gap-1"
                  >
                    <span>📍</span>
                    <span>
                      Reisedestination verwenden:{" "}
                      <span className="text-slate-400">{trip.destination}</span>
                    </span>
                  </button>
                )}
              </div>

              {error && <p className="text-xs text-red-400">{error}</p>}
            </div>
          )}

          {/* ─── Step: params › Schritt 2: Reisetyp & Mobilität ───────────── */}
          {step === "params" && paramStep === 2 && (
            <div className="space-y-5">
              <div>
                <label className="block mb-1.5 text-xs text-slate-400 font-medium">
                  Art der Reise bzw. Aktivitäten <span className="text-slate-600">(optional)</span>
                </label>
                <input
                  type="text"
                  value={reiseArt}
                  onChange={(e) => setReiseArt(e.target.value)}
                  placeholder="z.B. Familienurlaub, Freunde-Städtetrip, Vereinsreise"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block mb-2 text-xs text-slate-400 font-medium">
                  Mobilität <span className="text-slate-600">(optional, Mehrfachauswahl)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {MOBILITAET_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleMobilitaet(opt.value)}
                      className={chipClass(mobilitaet.includes(opt.value))}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block mb-2 text-xs text-slate-400 font-medium">Budget</label>
                <div className="flex flex-wrap gap-2">
                  {BUDGET_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setBudget(opt.value)}
                      className={chipClass(budget === opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── Step: params › Schritt 3: Teilnehmer & Interessen ────────── */}
          {step === "params" && paramStep === 3 && (
            <div className="space-y-5">
              <div>
                <label className="block mb-1.5 text-xs text-slate-400 font-medium">
                  Teilnehmer <span className="text-slate-600">(optional)</span>
                </label>
                <input
                  type="text"
                  value={teilnehmerBeschreibung}
                  onChange={(e) => setTeilnehmerBeschreibung(e.target.value)}
                  placeholder="z.B. 4 Erwachsene, 2 Kinder (8 und 12 Jahre)"
                  className={`${inputClass} mb-2`}
                />
                <div className="flex flex-wrap gap-2 mb-2">
                  {TEILNEHMER_KATEGORIEN.map((kat) => (
                    <button
                      key={kat.value}
                      type="button"
                      onClick={() => toggleTeilnehmerKategorie(kat.value)}
                      className={chipClass(teilnehmerKategorien.includes(kat.value))}
                    >
                      {kat.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  {[
                    { value: "gemischt", label: "Gemischt" },
                    { value: "nur_frauen", label: "Nur Frauen" },
                    { value: "nur_maenner", label: "Nur Männer" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setTeilnehmerGeschlecht(opt.value)}
                      className={chipClass(teilnehmerGeschlecht === opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block mb-2 text-xs text-slate-400 font-medium">
                  Interessen <span className="text-slate-600">(optional)</span>
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {INTERESSEN_VORSCHLAEGE.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleInteresse(tag)}
                      className={chipClass(interessen.includes(tag))}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={interessenInput}
                    onChange={(e) => setInteressenInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addInteresseFromInput()}
                    placeholder="Eigenes Interesse tippen + Enter"
                    className="flex-1 bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
                  />
                  <button
                    type="button"
                    onClick={addInteresseFromInput}
                    className="px-3 py-2 bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-400 hover:text-slate-200 text-sm transition-colors"
                  >
                    +
                  </button>
                </div>
                {interessen.filter((i) => !INTERESSEN_VORSCHLAEGE.includes(i)).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {interessen
                      .filter((i) => !INTERESSEN_VORSCHLAEGE.includes(i))
                      .map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-3 py-1.5 rounded-full bg-sky-500/20 text-sky-400 border border-sky-500/30 flex items-center gap-1"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => toggleInteresse(tag)}
                            className="text-sky-600 hover:text-sky-400 ml-0.5"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Step: params › Schritt 4: Details ────────────────────────── */}
          {step === "params" && paramStep === 4 && (
            <div className="space-y-5">
              {/* Übernachtungen planen */}
              <div>
                <label className="block mb-2 text-xs text-slate-400 font-medium">
                  Übernachtungen einplanen <span className="text-slate-600">(optional)</span>
                </label>
                <div className="flex gap-2">
                  {[
                    { value: false, label: "Nein" },
                    { value: true, label: "Ja" },
                  ].map((opt) => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setUebernachtungenPlanen(opt.value)}
                      className={chipClass(uebernachtungenPlanen === opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {uebernachtungenPlanen && (
                  <div className="mt-2 bg-sky-500/10 border border-sky-500/20 rounded-xl px-3 py-2">
                    <p className="text-xs text-sky-400">
                      Die KI plant Unterkunfts-Empfehlungen pro Nacht ein. Diese erscheinen als Programmpunkte.
                    </p>
                  </div>
                )}
              </div>

              {/* Essen planen */}
              <div>
                <label className="block mb-2 text-xs text-slate-400 font-medium">
                  Mahlzeiten einplanen <span className="text-slate-600">(optional, Mehrfachauswahl)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {ESSEN_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleEssen(opt.value)}
                      className={chipClass(essenPlanen.includes(opt.value))}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fixe Punkte */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-slate-400 font-medium">
                    Fixe Termine <span className="text-slate-600">(optional)</span>
                  </label>
                  {fixePunkte.length < 5 && (
                    <button
                      type="button"
                      onClick={addFixerPunkt}
                      className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
                    >
                      + Hinzufügen
                    </button>
                  )}
                </div>
                {fixePunkte.length === 0 && (
                  <p className="text-xs text-slate-600 italic">
                    z.B. Fähre gebucht, Konzert, Hotel-Check-in
                  </p>
                )}
                <div className="space-y-3">
                  {fixePunkte.map((punkt, idx) => (
                    <div key={idx} className="bg-[#1e2639] rounded-xl p-3 relative">
                      <button
                        type="button"
                        onClick={() => removeFixerPunkt(idx)}
                        className="absolute top-2 right-3 text-slate-600 hover:text-red-400 text-sm transition-colors"
                      >
                        ×
                      </button>
                      <input
                        type="text"
                        value={punkt.beschreibung}
                        onChange={(e) => updateFixerPunkt(idx, "beschreibung", e.target.value)}
                        placeholder="Was muss wann passieren?"
                        className="w-full bg-transparent border-b border-[#2a3347] text-slate-100 py-1 text-sm mb-2 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
                      />
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={punkt.datum}
                          onChange={(e) => updateFixerPunkt(idx, "datum", e.target.value)}
                          className="flex-1 bg-[#161b27] border border-[#2a3347] rounded-lg text-slate-100 px-2 py-1 text-xs focus:outline-none focus:border-sky-500/50"
                        />
                        <input
                          type="time"
                          value={punkt.zeit}
                          onChange={(e) => updateFixerPunkt(idx, "zeit", e.target.value)}
                          className="w-24 bg-[#161b27] border border-[#2a3347] rounded-lg text-slate-100 px-2 py-1 text-xs focus:outline-none focus:border-sky-500/50"
                        />
                        <input
                          type="text"
                          value={punkt.ort}
                          onChange={(e) => updateFixerPunkt(idx, "ort", e.target.value)}
                          placeholder="Ort (optional)"
                          className="flex-1 bg-[#161b27] border border-[#2a3347] rounded-lg text-slate-100 px-2 py-1 text-xs focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rahmenbedingungen */}
              <div>
                <label className="block mb-1.5 text-xs text-slate-400 font-medium">
                  Rahmenbedingungen <span className="text-slate-600">(optional)</span>
                </label>
                <textarea
                  value={rahmenbedingungen}
                  onChange={(e) => setRahmenbedingungen(e.target.value)}
                  placeholder="z.B. Max. 4h Fahrt pro Tag, vegetarisches Essen, Senior mit eingeschränkter Mobilität"
                  rows={3}
                  className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600 resize-none"
                />
              </div>

              {error && <p className="text-xs text-red-400">{error}</p>}
            </div>
          )}

          {/* ─── Step: loading ────────────────────────────────────────────── */}
          {step === "loading" && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="w-10 h-10 rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
              <p className="text-sm text-slate-400 text-center px-4 min-h-[2.5rem] transition-all">
                {loadingMessages.current[loadingMsgIdx] ?? "KI plant Eure Reise…"}
              </p>
              <p className="text-xs text-slate-600">Das kann bis zu 2 Minuten dauern.</p>
            </div>
          )}

          {/* ─── Step: review ─────────────────────────────────────────────── */}
          {step === "review" && plan && (
            <div className="space-y-5">
              {plan.tage.map((tag, dayIdx) => {
                const vorhandeneHeute = vorhandeneAktivitaeten.filter(
                  (a) => a.datum === tag.datum
                );
                return (
                <div key={tag.datum}>
                  <h3 className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-2 px-1">
                    {format(new Date(tag.datum + "T12:00:00"), "EEEE, dd. MMMM", { locale: de })}
                  </h3>

                  <div className="space-y-2">
                    {/* Bestehende Aktivitäten – grau, nicht wählbar */}
                    {vorhandeneHeute.map((va, idx) => (
                      <div
                        key={`existing-${idx}`}
                        className="flex items-start gap-3 rounded-xl p-3 bg-[#161b27] border border-[#2a3347] opacity-50 cursor-default select-none"
                      >
                        <div className="w-5 h-5 rounded-full border-2 border-slate-700 flex items-center justify-center shrink-0 mt-0.5">
                          <div className="w-2 h-2 rounded-full bg-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">
                              Bereits geplant
                            </span>
                            {va.start_zeit && (
                              <span className="text-[10px] text-slate-500">
                                {new Date(va.start_zeit).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}
                                {va.end_zeit && ` – ${new Date(va.end_zeit).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}`}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-semibold text-slate-500">{va.titel}</p>
                          {va.ort && <p className="text-[10px] text-slate-600 mt-1">📍 {va.ort}</p>}
                        </div>
                      </div>
                    ))}

                    {/* KI-Vorschläge – wählbar */}
                    {tag.aktivitaeten.map((akt, aktIdx) => {
                      const mappedCat = CATEGORY_MAP[akt.kategorie] ?? "Sonstiges";
                      const badgeColor = CATEGORY_COLORS[mappedCat] ?? "bg-slate-500";

                      return (
                        <div
                          key={aktIdx}
                          onClick={() => toggleActivity(dayIdx, aktIdx)}
                          className={`flex items-start gap-3 rounded-xl p-3 cursor-pointer transition-colors ${
                            akt.accepted
                              ? "bg-[#1e2639] border border-[#2a3347]"
                              : "bg-[#161b27] border border-[#2a3347] opacity-50"
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                              akt.accepted
                                ? "border-sky-400 bg-sky-400/20"
                                : "border-slate-600"
                            }`}
                          >
                            {akt.accepted && (
                              <div className="w-2 h-2 rounded-full bg-sky-400" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full text-white ${badgeColor}`}>
                                {mappedCat}
                              </span>
                              {akt.zeit_von && akt.zeit_bis && (
                                <span className="text-[10px] text-slate-500">
                                  {akt.zeit_von} – {akt.zeit_bis}
                                </span>
                              )}
                              {akt.prioritaet === "muss" && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400">
                                  Fix
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-slate-100">{akt.titel}</p>
                            {akt.beschreibung && (
                              <p className="text-xs text-slate-400 mt-0.5 leading-relaxed line-clamp-2">
                                {akt.beschreibung}
                              </p>
                            )}
                            {akt.ort && (
                              <p className="text-[10px] text-sky-400 mt-1">📍 {akt.ort}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })}

              {error && <p className="text-xs text-red-400">{error}</p>}
            </div>
          )}
          </>)}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#2a3347] shrink-0 flex gap-2">
          {step === "params" && (
            <>
              {paramStep > 1 ? (
                <button
                  onClick={() => setParamStep((s) => s - 1)}
                  className="flex-1 py-2.5 rounded-xl bg-[#1e2639] text-slate-300 text-sm hover:bg-[#2a3347] transition-colors"
                >
                  ← Zurück
                </button>
              ) : (
                <button
                  onClick={() => onClose()}
                  className="flex-1 py-2.5 rounded-xl bg-[#1e2639] text-slate-300 text-sm hover:bg-[#2a3347] transition-colors"
                >
                  Abbrechen
                </button>
              )}

              {paramStep < PARAM_STEPS ? (
                <button
                  onClick={() => setParamStep((s) => s + 1)}
                  disabled={paramStep === 1 && !step1Valid}
                  className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Weiter →
                </button>
              ) : (
                <div className="flex-1 space-y-1.5">
                  {creditBalance !== null && (
                    <p className="text-[10px] text-slate-600 text-center">
                      Kosten: {PLANNING_COST_CREDITS} SC · Verbleibend: {Math.max(0, creditBalance - PLANNING_COST_CREDITS)} SC
                    </p>
                  )}
                  <button
                    onClick={handleGenerate}
                    disabled={creditBalance !== null && creditBalance < PLANNING_COST_CREDITS}
                    className="w-full py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ✨ Generieren
                  </button>
                </div>
              )}
            </>
          )}

          {step === "review" && plan && (
            <>
              <button
                onClick={() => { setStep("params"); setError(""); setParamStep(1); }}
                className="flex-1 py-2.5 rounded-xl bg-[#1e2639] text-slate-300 text-sm hover:bg-[#2a3347] transition-colors"
              >
                ← Neu generieren
              </button>
              <button
                onClick={handleConfirm}
                disabled={saving || acceptedCount === 0}
                className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
              >
                {saving
                  ? "Wird gespeichert…"
                  : `${acceptedCount} ${acceptedCount === 1 ? "Aktivität" : "Aktivitäten"} übernehmen`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>

    {showPurchaseModal && (
      <CreditPurchaseModal
        currentBalance={creditBalance}
        onClose={() => { setShowPurchaseModal(false); reloadCredits(); }}
      />
    )}
    </>
  );
}

